import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Recording } from '../../types';
import { RecordingService } from '../../lib/firebase/recordingService';
import { usePlayerStore } from './playerStore';
import { PlayerControls } from './PlayerControls';
import { CanvasPlayer } from './CanvasPlayer';
import { HybridAudioSync } from './HybridAudioSync';
import { TouchGestures } from './TouchGestures';
import { PerformanceMonitor } from './PerformanceMonitor';
import { X, Loader2 } from 'lucide-react';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

interface RecordingPlayerProps {
  recording: Recording;
  onClose: () => void;
}

export const RecordingPlayer: React.FC<RecordingPlayerProps> = ({ 
  recording, 
  onClose 
}) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audioFormatWarning, setAudioFormatWarning] = useState(false);
  
  const { setRecording, isFullscreen, reset, setShowControls, hasAudioError } = usePlayerStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useDeviceDetection();
  
  // Define onError callback early to avoid hooks order issues
  const handleAudioError = useCallback(async (error: Error) => {
    
    // Try to get a fresh audio URL if blob expired
    if (error.message === 'Blob URL expired' && (recording.audioPath || recording.audioBlob)) {
      try {
        const pathForAudio = recording.audioPath || recording.id;
        const freshUrl = await RecordingService.getRecordingAudioUrl(pathForAudio);
        setAudioUrl(freshUrl);
      } catch (refreshError) {
        setError('Ses dosyası yüklenemedi');
      }
    }
  }, [recording.audioPath, recording.audioBlob, recording.id]);
  
  // Load recording data
  useEffect(() => {
    const loadRecording = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Set recording in store
        setRecording(recording);
        
        // Load audio if available
        if (recording.audioPath || recording.audioBlob) {
          try {
            // Use audioPath if available, otherwise fall back to id
            const pathForAudio = recording.audioPath || recording.id;
            const url = await RecordingService.getRecordingAudioUrl(pathForAudio);
            setAudioUrl(url);
          } catch (audioError) {
            // Continue without audio
          }
        }
        
        setLoading(false);
      } catch (err) {
        setError('Kayıt yüklenirken hata oluştu');
        setLoading(false);
      }
    };
    
    loadRecording();
    
    return () => {
      reset();
    };
  }, [recording, setRecording, reset]);
  
  // Handle fullscreen
  useEffect(() => {
    const container = document.getElementById('recording-player-container');
    if (!container) return;
    
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      if (isCurrentlyFullscreen !== isFullscreen) {
        usePlayerStore.setState({ isFullscreen: isCurrentlyFullscreen });
      }
    };
    
    if (isFullscreen && !document.fullscreenElement) {
      container.requestFullscreen().catch(() => {});
    } else if (!isFullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreen]);
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 flex flex-col items-center">
          <Loader2 className="animate-spin h-12 w-12 text-blue-500 mb-4" />
          <p className="text-gray-700">Kayıt yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <h3 className="text-red-600 font-semibold mb-2">Hata</h3>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      id="recording-player-container"
      className="fixed inset-0 bg-black z-50 flex flex-col"
    >
      {/* Header - Minimalist */}
      <div className="bg-gray-900/95 backdrop-blur text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Geri Butonu */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800/50 rounded-full transition-colors"
            title="Geri dön"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          
          {/* Soru Bilgileri - Kompakt */}
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">
              {recording.questionMetadata?.questionNo ? `Soru ${recording.questionMetadata.questionNo}` : 'Kayıt'}
            </span>
            {recording.questionMetadata?.testName && (
              <span className="text-gray-400 hidden sm:inline">• {recording.questionMetadata.testName}</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Canvas */}
      <div className="flex-1 relative bg-black overflow-hidden">
        <CanvasPlayer />
      </div>
      
      {/* Audio Format Warning */}
      {hasAudioError && !audioFormatWarning && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg shadow-lg z-10">
          <p className="text-sm">
            ⚠️ Ses formatı desteklenmiyor. Kayıt sessiz oynatılacak.
          </p>
          <button
            onClick={() => setAudioFormatWarning(true)}
            className="text-xs underline mt-1"
          >
            Tamam
          </button>
        </div>
      )}
      
      {/* Controls */}
      <PlayerControls className="border-t border-gray-800" />
      
      {/* Audio */}
      <HybridAudioSync 
        audioUrl={audioUrl} 
        onError={handleAudioError}
      />
      
      {/* Touch Gestures */}
      <TouchGestures 
        containerRef={containerRef}
        onShowControls={() => setShowControls(true)}
      />
      
      {/* Performance Monitor (Development Only) */}
      <PerformanceMonitor />
    </div>
  );
};